import { useDeferredValue, useMemo, useState } from "react";
import { useBreadcrumbs } from "@app/components/Breadcrumbs";
import {
  TableView,
  TableHeader,
  Column,
  TableBody,
  Row,
  Cell,
  Collection,
  Badge,
  ActionButton,
  Switch,
  Text,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  SearchField,
  Picker,
  PickerItem,
  DateRangePicker,
} from "@react-spectrum/s2";
import { useDateFormatter } from "react-aria";
import { today, getLocalTimeZone } from "@internationalized/date";
import RefreshIcon from "@react-spectrum/s2/icons/Refresh";
import DownloadIcon from "@react-spectrum/s2/icons/Download";
import { downloadStatusRecordsExcel } from "@app/services/adminReport";

import {
  useIntegrationsStatus,
  useRefreshIntegrationsStatus,
  useStatusRecords,
} from "@app/hooks/admin";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const columns = [
  { name: "Integration", id: "name", isRowHeader: true },
  { name: "Status", id: "status", width: 160 },
  { name: "Latency  ", id: "latencyMs", align: "end", width: 120 },
  { name: "Detail", id: "message" },
];

// Known fields of a `status` collection document, in display order. Any
// additional fields found in the data are appended after these.
const RECORD_COLUMNS = [
  { id: "name", name: "Name", isRowHeader: true, minWidth: 180 },
  { id: "status", name: "Status", minWidth: 140 },
  { id: "integrationId", name: "Integration ID", minWidth: 160 },
  { id: "method", name: "Method", minWidth: 120 },
  { id: "source", name: "Source", minWidth: 140 },
  { id: "endpoint", name: "Endpoint", minWidth: 240 },
  { id: "httpStatus", name: "HTTP status", align: "end", minWidth: 120 },
  { id: "latencyMs", name: "Latency (ms)", align: "end", minWidth: 130 },
  { id: "message", name: "Message", minWidth: 240 },
  { id: "error", name: "Error", minWidth: 240 },
  { id: "order", name: "Order", align: "end", minWidth: 90 },
  { id: "checkedAt", name: "Checked at", minWidth: 200 },
  { id: "createdAt", name: "Created at", minWidth: 200 },
  { id: "updatedAt", name: "Updated at", minWidth: 200 },
  { id: "_id", name: "_id", minWidth: 240 },
];

const DATE_FIELDS = new Set(["checkedAt", "createdAt", "updatedAt"]);

// Record fields intentionally not shown as columns.
const HIDDEN_RECORD_FIELDS = new Set([
  "id",
  "category",
  "configured",
  "complex",
]);

// Maps a probe status to a Spectrum Badge variant + human label.
const STATUS_BADGE = {
  operational: { variant: "positive", label: "Operational" },
  degraded: { variant: "notice", label: "Degraded" },
  down: { variant: "negative", label: "Down" },
  not_configured: { variant: "neutral", label: "Not configured" },
};

const panelStyle = style({
  display: "flex",
  flexDirection: "column",
  gap: 24,
  paddingTop: 16,
  minHeight: 0,
});

const tableStyle = style({ flexGrow: 1, minHeight: 0, marginTop: 16 });

const toolbarStyle = style({
  display: "flex",
  gap: 12,
  alignItems: "center",
  flexWrap: "wrap",
});

const detailTextStyle = style({ font: "detail", color: "gray-600" });

const ALL_STATUSES = "all";

const getTodayRange = () => {
  const day = today(getLocalTimeZone());
  return { start: day, end: day };
};

const compareValues = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
};

const ApiStatusPanel = () => {
  const [complexMode, setComplexMode] = useState(false);

  const { data, isLoading, isError, error } =
    useIntegrationsStatus(complexMode);

  const refresh = useRefreshIntegrationsStatus(complexMode);
  const isRefreshing = refresh.isPending;

  const formatter = useDateFormatter({
    dateStyle: "medium",
    timeStyle: "medium",
  });

  const rows = data?.integrations ?? [];
  const loadingState =
    isError || refresh.isError
      ? "error"
      : isLoading || isRefreshing
        ? "loading"
        : "idle";
  const checkedAt = data?.checkedAt ? new Date(data.checkedAt) : null;

  return (
    <>
      <div className={toolbarStyle}>
        <Switch isSelected={complexMode} onChange={setComplexMode}>
          Complex mode
        </Switch>
        <ActionButton
          isQuiet
          size="S"
          staticColor="auto"
          aria-label="Refresh integration status"
          isDisabled={isRefreshing}
          onPress={() => refresh.mutate()}
        >
          <RefreshIcon />
          <Text>{isRefreshing ? "Checking…" : "Refresh"}</Text>
        </ActionButton>
        {checkedAt ? (
          <Text styles={detailTextStyle}>
            Last checked {formatter.format(checkedAt)}
          </Text>
        ) : (
          !isLoading && (
            <Text styles={detailTextStyle}>
              Never checked — press Refresh to run the test
            </Text>
          )
        )}
      </div>

      <TableView
        aria-label="External API status"
        loadingState={loadingState}
        renderEmptyState={() =>
          isError
            ? (error?.message ?? "Failed to load integration status")
            : "No integrations found"
        }
        overflowMode="wrap"
        density="spacious"
        styles={tableStyle}
      >
        <TableHeader columns={columns}>
          {(column) => (
            <Column
              align={column.align}
              isRowHeader={column.isRowHeader}
              width={column.width}
              minWidth={column.minWidth}
            >
              {column.name}
            </Column>
          )}
        </TableHeader>
        <TableBody>
          <Collection items={rows}>
            {(integration) => {
              const badge =
                STATUS_BADGE[integration.status] ?? STATUS_BADGE.down;
              return (
                <Row id={integration.id}>
                  <Cell>{integration.name}</Cell>
                  <Cell>
                    <Badge size="S" variant={badge.variant}>
                      {badge.label}
                    </Badge>
                  </Cell>
                  <Cell align="end">
                    {integration.status === "not_configured"
                      ? "—"
                      : `${integration.latencyMs} ms`}
                  </Cell>
                  <Cell>{integration.message}</Cell>
                </Row>
              );
            }}
          </Collection>
        </TableBody>
      </TableView>
    </>
  );
};

const StatusRecordsPanel = () => {
  const { data, isLoading, isFetching, isError, error, refetch } =
    useStatusRecords();
  const [sortDescriptor, setSortDescriptor] = useState({
    column: "createdAt",
    direction: "descending",
  });

  const formatter = useDateFormatter({
    dateStyle: "medium",
    timeStyle: "medium",
  });

  const records = useMemo(() => data ?? [], [data]);

  const [nameFilter, setNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
  const [range, setRange] = useState(getTodayRange);
  const [isAllTime, setIsAllTime] = useState(false);
  const deferredName = useDeferredValue(nameFilter);

  const statusOptions = useMemo(() => {
    const ids = new Set(Object.keys(STATUS_BADGE));
    records.forEach((record) => record.status && ids.add(record.status));
    return [
      { id: ALL_STATUSES, label: "All statuses" },
      ...[...ids].map((id) => ({ id, label: STATUS_BADGE[id]?.label ?? id })),
    ];
  }, [records]);

  const filteredRecords = useMemo(() => {
    const needle = deferredName.trim().toLowerCase();
    const timeZone = getLocalTimeZone();
    const from =
      !isAllTime && range?.start ? range.start.toDate(timeZone) : null;
    // End of the selected day: start of the following day (exclusive).
    const to =
      !isAllTime && range?.end
        ? range.end.add({ days: 1 }).toDate(timeZone)
        : null;

    return records.filter((record) => {
      if (needle) {
        const haystack = `${record.name ?? ""} ${record.integrationId ?? ""}`;
        if (!haystack.toLowerCase().includes(needle)) return false;
      }
      if (statusFilter !== ALL_STATUSES && record.status !== statusFilter) {
        return false;
      }
      if (from || to) {
        const created = new Date(record.createdAt ?? record.checkedAt);
        if (Number.isNaN(created.getTime())) return false;
        if (from && created < from) return false;
        if (to && created >= to) return false;
      }
      return true;
    });
  }, [records, deferredName, statusFilter, range, isAllTime]);

  const recordColumns = useMemo(() => {
    const known = new Set([
      ...RECORD_COLUMNS.map((column) => column.id),
      ...HIDDEN_RECORD_FIELDS,
    ]);
    const extra = new Set();
    records.forEach((record) =>
      Object.keys(record).forEach((key) => {
        if (!known.has(key)) extra.add(key);
      }),
    );
    return [
      ...RECORD_COLUMNS,
      ...[...extra].sort().map((id) => ({ id, name: id, minWidth: 160 })),
    ].map((column) => ({ ...column, allowsSorting: true }));
  }, [records]);

  const sortedRecords = useMemo(() => {
    const { column, direction } = sortDescriptor ?? {};
    if (!column) return filteredRecords;
    const sorted = [...filteredRecords].sort((a, b) =>
      compareValues(a[column], b[column]),
    );
    return direction === "descending" ? sorted.reverse() : sorted;
  }, [filteredRecords, sortDescriptor]);

  const formatValue = (key, value) => {
    if (value === undefined || value === null || value === "") return "—";
    if (DATE_FIELDS.has(key)) {
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? String(value)
        : formatter.format(date);
    }
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  const renderCell = (record, column) => {
    if (column.id === "status" && record.status) {
      const badge = STATUS_BADGE[record.status] ?? {
        variant: "neutral",
        label: record.status,
      };
      return (
        <Badge size="S" variant={badge.variant}>
          {badge.label}
        </Badge>
      );
    }
    return formatValue(column.id, record[column.id]);
  };

  const loadingState = isError ? "error" : isLoading ? "loading" : "idle";

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadExcel = async () => {
    setIsDownloading(true);
    try {
      const datePart =
        !isAllTime && range?.start && range?.end
          ? `${range.start.toString()}-${range.end.toString()}`
          : "all-time";
      const fileName = `status-records-${statusFilter}-${datePart}.xlsx`;
      await downloadStatusRecordsExcel(sortedRecords, recordColumns, fileName);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <div className={toolbarStyle}>
        <ActionButton
          isQuiet
          size="S"
          staticColor="auto"
          aria-label="Reload status records"
          isDisabled={isFetching}
          onPress={() => refetch()}
        >
          <RefreshIcon />
          <Text>{isFetching ? "Loading…" : "Reload"}</Text>
        </ActionButton>
        <ActionButton
          isQuiet
          aria-label="Download status records"
          isPending={isDownloading}
          isDisabled={sortedRecords.length === 0 || isDownloading || isLoading}
          onPress={handleDownloadExcel}
        >
          <DownloadIcon />
        </ActionButton>
        {!isLoading && !isError && (
          <Text styles={detailTextStyle}>
            {filteredRecords.length} of {records.length}{" "}
            {records.length === 1 ? "record" : "records"}
          </Text>
        )}
      </div>

      <div className={toolbarStyle}>
        <SearchField
          aria-label="Filter by name"
          placeholder="Filter by name"
          value={nameFilter}
          onChange={setNameFilter}
          onClear={() => setNameFilter("")}
          styles={style({ width: 260 })}
        />
        <Picker
          aria-label="Filter by status"
          items={statusOptions}
          value={statusFilter}
          onChange={(key) => setStatusFilter(String(key ?? ALL_STATUSES))}
          styles={style({ width: 200 })}
        >
          {(item) => (
            <PickerItem id={item.id} textValue={item.label}>
              {item.label}
            </PickerItem>
          )}
        </Picker>
        <DateRangePicker
          aria-label="Filter by created date"
          value={range}
          onChange={(newRange) => {
            if (newRange) {
              setRange(newRange);
              setIsAllTime(false);
            }
          }}
          isDisabled={isAllTime}
          maxVisibleMonths={2}
          styles={style({ width: 260 })}
        />
        <Switch
          isSelected={isAllTime}
          onChange={(selected) => {
            setIsAllTime(selected);
            if (!selected && (!range?.start || !range?.end)) {
              setRange(getTodayRange());
            }
          }}
        >
          All time
        </Switch>
      </div>

      <TableView
        aria-label="Status records"
        loadingState={loadingState}
        renderEmptyState={() =>
          isError
            ? (error?.message ?? "Failed to load status records")
            : records.length > 0
              ? "No status records match the current filters"
              : "No status records found"
        }
        overflowMode="wrap"
        density="compact"
        sortDescriptor={sortDescriptor}
        onSortChange={setSortDescriptor}
        styles={tableStyle}
      >
        <TableHeader columns={recordColumns}>
          {(column) => (
            <Column
              id={column.id}
              align={column.align}
              isRowHeader={column.isRowHeader}
              allowsSorting={column.allowsSorting}
              width={column.width}
              minWidth={column.minWidth}
            >
              {column.name}
            </Column>
          )}
        </TableHeader>
        <TableBody items={sortedRecords} dependencies={[recordColumns]}>
          {(record) => (
            <Row
              id={record._id ?? `${record.integrationId}-${record.createdAt}`}
              columns={recordColumns}
            >
              {(column) => (
                <Cell align={column.align}>{renderCell(record, column)}</Cell>
              )}
            </Row>
          )}
        </TableBody>
      </TableView>
    </>
  );
};

export const IntegrationsStatus = () => {
  useBreadcrumbs([{ label: "Admin", href: "/admin" }, { label: "API Status" }]);

  return (
    <div
      className={style({
        display: "flex",
        flexDirection: "column",
        size: "full",
        maxHeight: "calc(100dvh - 56px - 48px)",
      })}
    >
      <title>API status | BeStory</title>

      <Tabs
        aria-label="Integrations"
        defaultSelectedKey="status"
        styles={style({ size: "full", minHeight: 0 })}
      >
        <TabList>
          <Tab id="status">API Status</Tab>
          <Tab id="records">Status records</Tab>
        </TabList>
        <TabPanel id="status" styles={panelStyle}>
          <ApiStatusPanel />
        </TabPanel>
        <TabPanel id="records" styles={panelStyle}>
          <StatusRecordsPanel />
        </TabPanel>
      </Tabs>
    </div>
  );
};
